/**
 * Seamless Pee Safe announcement marquee.
 * Batches clones without layout thrashing; animation starts after load + first measure.
 */
class PeeSafeAnnouncementMarquee extends HTMLElement {
  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {number} */
  #originalCount = 0;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #updateFrame;

  /** @type {(() => void) | undefined} */
  #onLoad;

  /** @type {boolean} */
  #loadComplete = false;

  /** @type {boolean} */
  #hasMetrics = false;

  connectedCallback() {
    this.#viewport = this.querySelector('[data-pee-safe-marquee-viewport]');
    this.#track = this.querySelector('[data-pee-safe-marquee-track]');

    if (!this.#viewport || !this.#track) return;

    this.#originalCount = this.#getOriginalItems().length;
    if (this.#originalCount <= 0) return;

    this.#resizeObserver = new ResizeObserver(() => this.#scheduleUpdate());
    this.#resizeObserver.observe(this.#viewport);

    this.#scheduleUpdate();

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.#scheduleUpdate());
    }

    this.#startAnimationOnLoad();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    if (this.#updateFrame) cancelAnimationFrame(this.#updateFrame);
    if (this.#onLoad) {
      window.removeEventListener('load', this.#onLoad);
      this.#onLoad = undefined;
    }
  }

  #startAnimationOnLoad() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onLoad = () => {
      this.#loadComplete = true;
      this.#maybeAnimate();
    };

    if (document.readyState === 'complete') {
      onLoad();
      return;
    }

    this.#onLoad = onLoad;
    window.addEventListener('load', this.#onLoad, { once: true });
  }

  #maybeAnimate() {
    if (!this.#loadComplete || !this.#hasMetrics) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#track?.classList.add('is-animating');
  }

  #scheduleUpdate() {
    if (this.#updateFrame) return;

    this.#updateFrame = requestAnimationFrame(() => {
      this.#updateFrame = undefined;
      this.#update();
    });
  }

  #getOriginalItems() {
    if (!this.#track) return [];
    return [...this.#track.querySelectorAll('[data-pee-safe-marquee-item]:not([data-pee-safe-marquee-clone])')];
  }

  #getCopySetCount() {
    if (!this.#track || this.#originalCount <= 0) return 0;
    return Math.floor(this.#track.children.length / this.#originalCount);
  }

  #measureSetWidth(originals) {
    if (!this.#track || this.#originalCount <= 0) return 0;

    const start = this.#track.children[0];
    const nextSet = this.#track.children[this.#originalCount];
    if (start instanceof HTMLElement && nextSet instanceof HTMLElement) {
      const width = nextSet.offsetLeft - start.offsetLeft;
      if (width > 0) return width;
    }

    const gap = parseFloat(getComputedStyle(this.#track).gap) || 0;
    let width = 0;
    for (const item of originals) {
      width += item.offsetWidth;
    }
    return width + gap * originals.length;
  }

  #appendCopySets(count, originals) {
    if (!this.#track || count <= 0) return;

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      originals.forEach((item) => {
        const clone = item.cloneNode(true);
        if (!(clone instanceof HTMLElement)) return;
        clone.setAttribute('data-pee-safe-marquee-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-pee-safe-marquee-item');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('a').forEach((link) => {
          link.setAttribute('tabindex', '-1');
        });
        fragment.appendChild(clone);
      });
    }
    this.#track.appendChild(fragment);
  }

  #trimCopySets(neededSets) {
    if (!this.#track || this.#originalCount <= 0) return;

    const keep = neededSets * this.#originalCount;
    while (this.#track.children.length > keep) {
      this.#track.lastElementChild?.remove();
    }
  }

  #update() {
    if (!this.#track || !this.#viewport) return;

    const originals = this.#getOriginalItems();
    if (originals.length === 0) return;

    const setWidth = this.#measureSetWidth(originals);
    const viewportWidth = this.#viewport.clientWidth;
    if (setWidth <= 0) return;

    let neededSets = Math.ceil((viewportWidth * 2) / setWidth);
    neededSets = Math.max(2, neededSets);
    if (neededSets % 2 !== 0) neededSets += 1;
    neededSets = Math.min(20, neededSets);

    const currentSets = this.#getCopySetCount();
    if (currentSets < neededSets) {
      this.#appendCopySets(neededSets - currentSets, originals);
    } else if (currentSets > neededSets) {
      this.#trimCopySets(neededSets);
    }

    const halfWidth = this.#track.scrollWidth / 2;
    if (halfWidth <= 0) return;

    const speed = parseFloat(this.getAttribute('speed') || '20');
    this.#track.style.setProperty('--pee-safe-marquee-shift', `${halfWidth}px`);
    this.#track.style.setProperty('--pee-safe-marquee-duration', `${Math.max(8, speed)}s`);

    this.#hasMetrics = true;
    this.#maybeAnimate();
  }
}

if (!customElements.get('pee-safe-announcement-marquee')) {
  customElements.define('pee-safe-announcement-marquee', PeeSafeAnnouncementMarquee);
}
